import React, { useState, useEffect } from 'react';
import {
  getUserId,
  getUserDetails,
  getUserReferrals,
  getMatrixDetails,
  getMatrixLevelUsers,
  getNormalLevelUsers,
  getMatrixLevelCounts,
  getNormalLevelCounts
} from '../utils/contract';
import { CoreIcon, CORE_COLORS } from './CoreIcons';
import './Team.css';

const CORE_NAMES = ['NeuroCore', 'AlgoCore', 'IntelliCore', 'SynapCore', 'DeepCore', 'DataCore', 'MindCore', 'QuantumCore'];
const PACKAGE_AMOUNTS = ['10', '25', '50', '100', '250', '500', '1000', '2500'];

function Team({ account }) {
  const [userDetails, setUserDetails] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [, setMatrixData] = useState({});
  const [matrixLevelCounts, setMatrixLevelCounts] = useState([]);
  const [normalLevelCounts, setNormalLevelCounts] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('matrix');
  const [expandedLevels, setExpandedLevels] = useState({});
  const [levelUsers, setLevelUsers] = useState({});
  const [loadingLevel, setLoadingLevel] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (account) {
      loadTeamData();
    }
  }, [account, selectedPackage]);

  const loadTeamData = async () => {
    setLoading(true);
    try {
      const userId = await getUserId(account);

      if (userId && userId !== '0') {
        const [details, refs, matrix, matrixCounts, normalCounts] = await Promise.all([
          getUserDetails(userId),
          getUserReferrals(userId),
          getMatrixDetails(userId, selectedPackage),
          getMatrixLevelCounts(userId, selectedPackage),
          getNormalLevelCounts(userId, selectedPackage)
        ]);

        setUserDetails(details);
        setReferrals(refs);
        setMatrixData(matrix);
        setMatrixLevelCounts(matrixCounts);
        setNormalLevelCounts(normalCounts);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLevel = async (level) => {
    const key = `${activeTab}-${level}`;

    if (expandedLevels[key]) {
      setExpandedLevels(prev => ({ ...prev, [key]: false }));
    } else {
      setExpandedLevels(prev => ({ ...prev, [key]: true }));

      if (!levelUsers[key]) {
        setLoadingLevel(level);
        try {
          const userId = await getUserId(account);
          let users = [];

          if (activeTab === 'matrix') {
            users = await getMatrixLevelUsers(userId, selectedPackage, level);
          } else {
            users = await getNormalLevelUsers(userId, selectedPackage, level);
          }

          setLevelUsers(prev => ({ ...prev, [key]: users }));
        } catch (error) {
          console.error('Error loading level users:', error);
        } finally {
          setLoadingLevel(null);
        }
      }
    }
  };

  const handlePackageSelect = (pkgId) => {
    setSelectedPackage(pkgId);
    setDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-screen">
          <img src="/t1.gif" alt="Loading" style={{ width: '120px', height: '120px' }} />
          <div className="loading"></div>
          <div className="loading-text">Loading network data...</div>
        </div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="container">
        <div className="t-empty">
          <div className="t-empty-icon">👥</div>
          <h2>No Network Data</h2>
          <p>Please register first to view your network</p>
        </div>
      </div>
    );
  }

  const totalMatrixMembers = matrixLevelCounts.reduce((sum, count) => sum + parseInt(count), 0);
  const totalGenerationMembers = normalLevelCounts.reduce((sum, count) => sum + parseInt(count), 0);
  const levelCounts = activeTab === 'matrix' ? matrixLevelCounts : normalLevelCounts;
  const levelWord = activeTab === 'matrix' ? 'Level' : 'Generation';
  const accent = CORE_COLORS[selectedPackage - 1];

  return (
    <div className="container team">
      <header className="page-hero">
        <div className="page-hero-text">
          <span className="page-hero-eyebrow">Neural Network</span>
          <h1 className="page-hero-title">Your Network</h1>
          <p className="page-hero-sub">View your AI network structure and team</p>
        </div>
        <div className="page-hero-id">
          <span>Core ID</span>
          <b>#{userDetails.id}</b>
        </div>
      </header>

      {/* Network stats */}
      <div className="t-stats">
        <div className="t-stat" style={{ '--sa': '#22d3ee' }}>
          <span className="t-stat-label">Matrix Members</span>
          <span className="t-stat-value">{totalMatrixMembers}</span>
        </div>
        <div className="t-stat" style={{ '--sa': '#a855f7' }}>
          <span className="t-stat-label">Generation Members</span>
          <span className="t-stat-value">{totalGenerationMembers}</span>
        </div>
        <div className="t-stat" style={{ '--sa': '#34f5c5' }}>
          <span className="t-stat-label">Direct Referrals</span>
          <span className="t-stat-value">{referrals.length}</span>
        </div>
      </div>

      {/* Direct referrals */}
      <div className="t-panel">
        <h3 className="t-panel-title">🎯 Direct Referrals <span className="t-count">{referrals.length}</span></h3>
        {referrals.length > 0 ? (
          <>
            <div className="t-chips">
              {referrals
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((refId) => (
                  <span key={refId} className="t-chip">#{refId}</span>
                ))}
            </div>
            {referrals.length > itemsPerPage && (
              <div className="t-pager">
                <button className="t-pager-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prev</button>
                <span className="t-pager-info">Page {currentPage} of {Math.ceil(referrals.length / itemsPerPage)}</span>
                <button className="t-pager-btn" onClick={() => setCurrentPage(p => Math.min(Math.ceil(referrals.length / itemsPerPage), p + 1))} disabled={currentPage === Math.ceil(referrals.length / itemsPerPage)}>Next →</button>
              </div>
            )}
          </>
        ) : (
          <div className="t-empty-inline">
            <div className="t-empty-icon">👥</div>
            <p>No direct referrals yet</p>
            <small>Share your referral link to grow your network</small>
          </div>
        )}
      </div>

      {/* Core selector */}
      <div className="t-panel t-select-panel">
        <h3 className="t-panel-title">Select Core System</h3>
        <div className="t-select-wrap">
          <button className="t-select" style={{ '--ca': accent }} onClick={() => setDropdownOpen(!dropdownOpen)}>
            <span className="t-select-gem"><CoreIcon index={selectedPackage - 1} /></span>
            <span className="t-select-name">{CORE_NAMES[selectedPackage - 1]}</span>
            <span className="t-select-price">${PACKAGE_AMOUNTS[selectedPackage - 1]}</span>
            <span className="t-select-caret">{dropdownOpen ? '▲' : '▼'}</span>
          </button>
          {dropdownOpen && (
            <div className="t-drop">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((pkgId) => (
                <div
                  key={pkgId}
                  className={`t-drop-item ${selectedPackage === pkgId ? 'selected' : ''}`}
                  style={{ '--ca': CORE_COLORS[pkgId - 1] }}
                  onClick={() => handlePackageSelect(pkgId)}
                >
                  <span className="t-drop-gem"><CoreIcon index={pkgId - 1} /></span>
                  <div className="t-drop-meta">
                    <span className="t-drop-name">{CORE_NAMES[pkgId - 1]}</span>
                    <span className="t-drop-price">${PACKAGE_AMOUNTS[pkgId - 1]} USDT</span>
                  </div>
                  {selectedPackage === pkgId && <span className="t-drop-check">✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs + levels */}
      <div className="t-panel">
        <div className="t-tabs">
          <button className={`t-tab ${activeTab === 'matrix' ? 'active' : ''}`} onClick={() => setActiveTab('matrix')}>🎯 Matrix Network</button>
          <button className={`t-tab ${activeTab === 'generation' ? 'active' : ''}`} onClick={() => setActiveTab('generation')}>🌐 Generation Network</button>
        </div>

        <h3 className="t-panel-title" style={{ marginTop: '4px' }}>
          {activeTab === 'matrix' ? 'Matrix Levels' : 'Generation Levels'}
          <span className="t-count">{activeTab === 'matrix' ? totalMatrixMembers : totalGenerationMembers} total</span>
        </h3>

        {levelCounts.map((count, index) => {
          const level = index + 1;
          const key = `${activeTab}-${level}`;
          const isExpanded = expandedLevels[key];
          const users = levelUsers[key] || [];

          return (
            <div key={level} className="t-level">
              <button className={`t-level-head ${isExpanded ? 'open' : ''}`} onClick={() => toggleLevel(level)}>
                <span className="t-level-name">{levelWord} {level}</span>
                <span className="t-level-right">
                  <span className="t-level-count">{count} members</span>
                  <span className="t-level-caret">{isExpanded ? '▼' : '▶'}</span>
                </span>
              </button>
              {isExpanded && (
                <div className="t-level-body">
                  {loadingLevel === level ? (
                    <div className="t-level-loading"><div className="loading"></div></div>
                  ) : users.length > 0 ? (
                    <div className="t-chips">
                      {users.map((userId) => (
                        <span key={userId} className="t-chip sm">#{userId}</span>
                      ))}
                    </div>
                  ) : (
                    <div className="t-empty-inline"><div className="t-empty-icon">👥</div><p>No members at this {levelWord.toLowerCase()}</p></div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Team;
